import { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { CircularProgress, Box } from '@mui/material';

/**
 * Component to protect routes that require authentication
 * Redirects to login if user is not authenticated
 */
const PrivateRoute = ({ children }) => {
  const { isAuthenticated, isLoading: globalLoading, checkAuth } = useAuth();
  const [localLoading, setLocalLoading] = useState(true);
  const [authVerified, setAuthVerified] = useState(false);
  const location = useLocation();

  // On mount, verify authentication status
  useEffect(() => {
    // Set a timeout to prevent infinite loading
    const loadingTimeout = setTimeout(() => {
      // If still loading after timeout, redirect to login
      if (localLoading && !isAuthenticated) {
        console.log('Auth verification timed out, redirecting to login');
        setLocalLoading(false);
      }
    }, 3000); // 3 second timeout
    
    const verifyAuth = async () => {
      if (!isAuthenticated) {
        try {
          console.log('Verifying authentication status...');
          const isValid = await checkAuth();
          console.log('Auth verification result:', isValid);
          setAuthVerified(true);
        } catch (error) {
          console.error('Auth verification failed:', error);
        } finally {
          setLocalLoading(false);
        }
      } else {
        // Already authenticated, no need to load
        setLocalLoading(false);
        setAuthVerified(true);
      }
    };
    
    verifyAuth();
    
    // Clean up timeout
    return () => clearTimeout(loadingTimeout);
  }, [isAuthenticated, checkAuth, localLoading]); // Added localLoading to dependency array

  // Check if we should show loading state (only briefly)
  const showLoading = (globalLoading || localLoading) && !authVerified;
  
  // Show loading state while checking authentication, but with a maximum duration
  if (showLoading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          minHeight: '60vh'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // If not authenticated after verification attempts, redirect to login
  if (!isAuthenticated) {
    console.log('User not authenticated, redirecting to login');
    // Store the current location to redirect back after login
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  // If authenticated, render the protected component
  return children;
};

export default PrivateRoute;